import { useEffect } from 'react';

/**
 * Hook to prevent "rubber-band" scrolling effects and unwanted scrolling behavior on mobile devices.
 *
 * This hook:
 * - Prevents page body scrolling when interacting with the chat interface
 * - Allows scrolling within the chat body when content is scrollable
 * - Prevents vertical scrolling on sample questions (horizontal scroll only)
 * - Handles touch events to determine scroll direction and apply appropriate behaviors
 */
export const useChatWindowLockScroll = (): void => {
  useEffect(() => {
    let startX = 0;
    let startY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }
    };

    const preventRubberBand = (e: TouchEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }

      const chatBodyEl = target.closest('#chat-body') as HTMLElement | null;
      const sampleQuestionsEl = target.closest('#chat-sample-questions-container') as HTMLElement | null;
      const chatBetaEl = target.closest('#chat-beta-container') as HTMLElement | null;

      if (sampleQuestionsEl) {
        const dx = e.touches[0].clientX - startX;
        const dy = e.touches[0].clientY - startY;

        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        const isMostlyVertical = absDy > absDx;

        if (isMostlyVertical) {
          e.preventDefault();
        }

        return;
      }

      if (chatBodyEl) {
        const canScroll = chatBodyEl.scrollHeight > chatBodyEl.clientHeight;
        if (!canScroll) {
          e.preventDefault();
        }
      } else if (!chatBetaEl) {
        e.preventDefault();
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', preventRubberBand, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', preventRubberBand);
    };
  }, []);
};
